/**
 * The Finance screen's arithmetic, which is almost none: every figure comes
 * from the desk, already computed by the ledger. What happens here is choosing
 * a column, formatting it, and saying plainly when a figure does not exist.
 *
 * TWO RULES THAT ARE TESTED (test/finance.test.ts):
 *   - a missing FX rate reads "no rate", never $0. A wholesale cost of zero is
 *     a claim; a missing rate is an absence, and the screen must not turn one
 *     into the other.
 *   - the AUD ex / inc toggle switches COLUMNS. It never derives one from the
 *     other: cash in and balances are reported inc only, and in ex mode they
 *     say "inc only" rather than dividing by 1.1.
 */
import type { FinanceMonth } from './api';

export type Mode = 'ex' | 'inc';

const AUD = new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 });
const AUD_CENTS = new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', minimumFractionDigits: 2 });

/** "$1,901", "−$425"; null is a dash. */
export function aud(n: number | null | undefined, { cents = false } = {}): string {
  if (n == null || Number.isNaN(n)) return '—';
  const s = (cents ? AUD_CENTS : AUD).format(Math.abs(n));
  return n < 0 ? `−${s}` : s;
}

/** "NZ$653.16". */
export function nzd(n: number | null | undefined): string {
  if (n == null) return '—';
  return `${n < 0 ? '−' : ''}NZ$${Math.abs(n).toFixed(2)}`;
}

/** An AUD figure that needed a rate: "no rate" when there was none, whatever the value says. */
export function audAtRate(n: number | null | undefined, rate: number | null | undefined, opts = {}): string {
  if (rate == null) return 'no rate';
  return aud(n, opts);
}

/** "@ 0.9000 · operator default" / "@ 0.9203 · bank rate of 15 Sep" / "no rate". */
export function rateLabel(rate: number | null, source: string | null, effective: string | null): string {
  if (rate == null || source === 'none' || source == null) return 'no rate';
  const from = source === 'fx_rates' ? `rate of ${effective}` : source;
  return `@ ${rate.toFixed(4)} · ${from}`;
}

/** Fields the portal reports inc GST only: in ex mode they are shown inc and labelled, never divided. */
export const INC_ONLY = new Set(['cash_in', 'prepaid_liability', 'debt_exposure']);

/** One column of a month for the toggle. `inc_only` says the ex figure does not exist. */
export function column(row: FinanceMonth | null | undefined, base: string, mode: Mode): { value: number | null; incOnly: boolean } {
  if (!row) return { value: null, incOnly: false };
  const r = row as unknown as Record<string, number | null>;
  if (INC_ONLY.has(base)) return { value: r[`${base}_inc`] ?? null, incOnly: mode === 'ex' };
  return { value: r[`${base}_${mode}`] ?? null, incOnly: false };
}

/** Charges applied = recurring + usage (Finance F1), less what was charged to accounts closed at the time (F3,
 *  migration 050: it will never arrive) and to accounts that have never paid (F4, 051: trial credit consumed), in the
 *  column the toggle names. The cards, the trend and margin read this. */
export function chargesApplied(row: FinanceMonth | null | undefined, mode: Mode = 'ex'): number | null {
  if (!row) return null;
  const rec = mode === 'ex' ? row.charges_recurring_ex : row.charges_recurring_inc;
  const use = mode === 'ex' ? row.charges_usage_ex : row.charges_usage_inc;
  const unc = (mode === 'ex' ? row.charges_uncollectable_ex : row.charges_uncollectable_inc) ?? 0;
  const trial = (mode === 'ex' ? row.charges_trial_credit_ex : row.charges_trial_credit_inc) ?? 0;
  return rec == null || use == null ? null : Math.round((rec + use - unc - trial) * 100) / 100;
}

/** Gross margin as a share of collectable charges (ex), as the ledger defines it. */
export function marginPct(row: FinanceMonth | null | undefined): number | null {
  const c = chargesApplied(row, 'ex');
  return row?.gross_margin_aud == null || !c ? null : row.gross_margin_aud / c;
}

/** "▲ 12% vs Aug" against the previous Hero month; null when either side is missing. */
export function delta(now: number | null, before: number | null, { points = false } = {}): { text: string; tone: 'up' | 'down' | 'flat' } | null {
  if (now == null || before == null) return null;
  if (points) {
    const d = (now - before) * 100;
    if (Math.abs(d) < 0.05) return { text: '— level', tone: 'flat' };
    return { text: `${d > 0 ? '▲' : '▼'} ${Math.abs(d).toFixed(1)} pts`, tone: d > 0 ? 'up' : 'down' };
  }
  if (before === 0) return now === 0 ? { text: '— level', tone: 'flat' } : null;
  const pct = ((now - before) / Math.abs(before)) * 100;
  if (Math.abs(pct) < 0.5) return { text: '— level', tone: 'flat' };
  return { text: `${pct > 0 ? '▲' : '▼'} ${Math.abs(Math.round(pct))}%`, tone: pct > 0 ? 'up' : 'down' };
}

const LONG = new Intl.DateTimeFormat('en-AU', { month: 'long', year: 'numeric', timeZone: 'UTC' });
const SHORT = new Intl.DateTimeFormat('en-AU', { month: 'short', timeZone: 'UTC' });
/** "September 2026" */
export const monthLong = (m: string) => LONG.format(new Date(`${m}-01T00:00:00Z`));
/** "Sep" */
export const monthShort = (m: string) => SHORT.format(new Date(`${m}-01T00:00:00Z`)).replace('Sept', 'Sep');

/** Balances were first observed on 22 Sep 2026; a month that ended before then has none. */
export const BALANCES_FROM = '2026-09';
export const balancesObserved = (m: string) => m >= BALANCES_FROM;

/** Debt split from debt_by_arrears_json: in a ladder (any cycle) and not. */
export function debtSplit(row: FinanceMonth | null | undefined): { ladder: number | null; noLadder: number | null } {
  if (!row?.debt_by_arrears_json) return { ladder: null, noLadder: null };
  const d = JSON.parse(row.debt_by_arrears_json) as Record<string, number>;
  const noLadder = d.no_cycle ?? 0;
  const ladder = Object.entries(d).filter(([k]) => k !== 'no_cycle').reduce((a, [, v]) => a + v, 0);
  return { ladder: Math.round(ladder * 100) / 100, noLadder };
}

/** D1 rows as a reader says them: 2.21 B, 48.3 M, 912 k, 640 (PSA #290). */
export function rows(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return '—';
  const [div, unit] = n >= 1e9 ? [1e9, ' B'] : n >= 1e6 ? [1e6, ' M'] : n >= 1e3 ? [1e3, ' k'] : [1, ''];
  const v = n / div;
  return `${div === 1 ? Math.round(v) : v >= 100 ? Math.round(v) : v.toFixed(v >= 10 ? 1 : 2)}${unit}`;
}
